import type { TFunction } from 'i18next';
import type { FilterFieldOption } from '~/components/ui';
import { getStockHealthOptions } from '~/utils/inventory';
import {
  INVENTORY_ENUM_OPERATORS,
  INVENTORY_NUMBER_OPERATORS,
  INVENTORY_STRING_OPERATORS,
  newInventoryFilterRowId,
  type InventoryFilterRow,
} from './buyerInventoryFilterStorage';

export const DEFAULT_INVENTORY_FILTER_ROW: Omit<InventoryFilterRow, 'id'> = {
  field: 'productCode',
  operator: 'contains',
  value: '',
  connector: 'and',
};

type InventoryTranslate = TFunction<'inventory'>;

const operatorLabelKeys = {
  contains: 'contains',
  eq: 'is',
  neq: 'is_not',
  notContain: 'does_not_contain',
  gte: 'gte',
  gt: 'gt',
  lte: 'lte',
  lt: 'lt',
} as const;

const operatorOptionsFor = (
  t: InventoryTranslate,
  operators: readonly (keyof typeof operatorLabelKeys)[]
): FilterFieldOption['operatorOptions'] =>
  operators.map((operator) => ({
    label: t(`inventory.filter.operators.${operatorLabelKeys[operator]}`),
    operator,
  }));

const textOperators = (t: InventoryTranslate) =>
  operatorOptionsFor(t, INVENTORY_STRING_OPERATORS);
const numberOperators = (t: InventoryTranslate) =>
  operatorOptionsFor(t, INVENTORY_NUMBER_OPERATORS);

export function getInventoryFilterFields(
  t: InventoryTranslate
): FilterFieldOption[] {
  return [
    {
      field: 'productName',
      label: t('inventory.filter.fields.product_name'),
      operatorOptions: textOperators(t),
      inputType: 'string',
    },
    {
      field: 'productCode',
      label: t('inventory.filter.fields.product_code'),
      operatorOptions: textOperators(t),
      inputType: 'string',
    },
    {
      field: 'length',
      label: t('inventory.filter.fields.length'),
      operatorOptions: textOperators(t),
      inputType: 'string',
    },
    {
      field: 'location',
      label: t('inventory.filter.fields.location'),
      operatorOptions: textOperators(t),
      inputType: 'string',
    },
    {
      field: 'branch',
      label: t('inventory.filter.fields.branch'),
      operatorOptions: textOperators(t),
      inputType: 'string',
    },
    {
      field: 'stock',
      label: t('inventory.filter.fields.stock'),
      operatorOptions: numberOperators(t),
      inputType: 'number',
    },
    {
      field: 'usageRatePerWeek',
      label: t('inventory.filter.fields.usage_rate_per_week'),
      operatorOptions: numberOperators(t),
      inputType: 'number',
    },
    {
      field: 'status',
      label: t('inventory.filter.fields.status'),
      operatorOptions: operatorOptionsFor(t, INVENTORY_ENUM_OPERATORS),
      inputType: 'select',
      selectOptions: getStockHealthOptions(t),
    },
  ];
}

export function initialInventoryValueFor(field: string): string {
  return field === 'status' ? 'HEALTHY' : '';
}

export function newInventoryFilterRow(): InventoryFilterRow {
  return { ...DEFAULT_INVENTORY_FILTER_ROW, id: newInventoryFilterRowId() };
}
